import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSafeArea } from '@/hooks/use-native';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { NotificationBadge } from '@/components/notifications/NotificationBadge';

interface HeaderProps {
  title: string;
  showBackButton?: boolean;
  transparent?: boolean;
}

const getHelpContent = (pathname: string) => {
  switch (pathname) {
    case '/':
      return {
        title: 'About Mining Matters',
        content: 'Report and track mining grievance cases in your community. Developed by Develop Digitally Limited on behalf of the Mines and Geology Division (MGD), Ministry of Agriculture, Fisheries and Mining. The app is built and hosted by Develop Digitally Limited — grievances are reviewed and responded to by MGD. Official source: mgd.gov.jm'
      };
    case '/report':
      return {
        title: 'Report Grievance',
        content: 'Choose how to report a mining-related issue. "File a New Grievance" lets you submit a detailed case report. "Add Supporting Photo" allows you to attach visual evidence to existing cases.'
      };
    case '/add-case':
      return {
        title: 'Add New Case',
        content: 'Fill out the form to report a new mining grievance. Provide as much detail as possible including title, description, location, and company involved. All fields help authorities understand and address the issue.'
      };
    case '/add-photo':
      return {
        title: 'Add Photo Evidence',
        content: 'Capture or upload photos to support your grievance case. Use "Take Photo" to capture new images or "Upload Photo" to select from your device. Photos provide crucial visual evidence for investigations.'
      };
    default:
      if (pathname.startsWith('/case/')) {
        return {
          title: 'Case Details',
          content: 'View detailed information about this specific grievance case including status, description, and timeline. Check back regularly for updates on the investigation progress.'
        };
      }
      if (pathname.includes('/updates')) {
        return {
          title: 'Case Updates',
          content: 'View the complete timeline of updates and progress for this case. Updates show how the case is being handled and any actions taken by authorities.'
        };
      }
      if (pathname === '/notifications') {
        return {
          title: 'Notifications',
          content: 'View push notifications about case status changes and updates. Tap a notification to open the related case. Notifications are stored on this device.'
        };
      }
      return {
        title: 'Help',
        content: 'This screen helps you navigate and use the Mining Grievance Tracker effectively. Each screen has specific functions to help report and track mining-related issues in your community.'
      };
  }
};

const Header: React.FC<HeaderProps> = ({ 
  title, 
  showBackButton = false,
  transparent = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const safeArea = useSafeArea();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goBack = () => {
    if (location.pathname !== '/') {
      navigate(-1);
    }
  };

  const helpContent = getHelpContent(location.pathname);

  return (
    <header 
      className={cn(
        'fixed top-0 left-0 right-0 z-50 py-3 px-4 transition-all duration-300 ease-in-out',
        scrolled ? 'bg-background/80 backdrop-blur-md shadow-sm' : 
                  transparent ? 'bg-transparent' : 'bg-background'
      )}
      style={{ paddingTop: `calc(${safeArea.top}px + 0.75rem)` }}
    >
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          {showBackButton && (
            <button 
              onClick={goBack}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm border border-border hover:bg-muted transition-colors duration-200"
              aria-label="Go back"
            >
              <ChevronLeft className="w-5 h-5 text-foreground" />
            </button>
          )}
          
          <h1 className={cn(
            "font-medium tracking-tight transition-all duration-300",
            scrolled ? "text-base" : "text-lg"
          )}>
            {title}
          </h1>
        </div>
        
        <div className="flex items-center gap-1">
          <NotificationBadge />
          <Popover>
          <PopoverTrigger asChild>
            <button 
              className="w-10 h-10 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-sm border border-border hover:bg-muted transition-colors duration-200"
              aria-label="About this screen"
            >
              <Info className="w-4 h-4 text-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">{helpContent.title}</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {helpContent.content}
              </p>
            </div>
          </PopoverContent>
        </Popover>
        </div>
      </div>
    </header>
  );
};

export default Header;
